import { DateTime } from 'luxon';

import {
  DateTimeValue,
  DateValue,
  NumberValue,
  RangeValue,
  TextValue,
  ValueType,
} from 'dashboard/models/graph-query';

export type ValueTypeToValue = {
  [ValueType.Date]: DateValue;
  [ValueType.DateTime]: DateTimeValue;
  [ValueType.Number]: NumberValue;
  [ValueType.Range]: RangeValue;
  [ValueType.Text]: TextValue;
};

export type DateTimeValueRange = [DateTimeValue, DateTimeValue];
export type Value = ValueTypeToValue[ValueType] | [DateTimeValue, DateTimeValue];

function extractCommonVariableData<VT extends ValueType, V extends ValueTypeToValue[VT]>(
  type: VT,
  rawValue: string,
): V {
  const result = {
    type: type,
    'raw-value': rawValue,
  } as V;
  const trimmedText = rawValue.trim();
  if (!(trimmedText.startsWith('{{') && trimmedText.endsWith('}}'))) {
    result.errored = false;
    result.variable = false;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    result.name = undefined as string;
    result.value = trimmedText;
  } else {
    const variableData = trimmedText.slice(2, -2).trim();
    const isInvalidName = (val: string) => val.includes(' ') || !!val.match(/[^a-z0-9_]/);

    // a variable with name syntax is in the following format: `{{ fav_coffee: linea }}`
    if (variableData.includes(':')) {
      const [trimmedName, trimmedValue] = variableData
        .split(/:([\s\S]*)/)
        .map((text) => text.trim());
      result.errored = isInvalidName(trimmedName);
      result.name = trimmedName;
      result.value = trimmedValue;
    } else {
      result.errored = isInvalidName(variableData);
      result.name = variableData;
      result.value = '';
    }

    result.variable = true;
  }

  return result;
}

export function processTextValue(textValue: TextValue): TextValue {
  return extractCommonVariableData(ValueType.Text, textValue['raw-value']);
}

export function processNumberValue(
  numberValue: NumberValue,
  controlConfig?: ControlConfigNumberPicker,
): NumberValue {
  const trimmed = numberValue['raw-value'].trim();
  // TODO(jim): Docs say `value` can be variable name, but for NumberValue it's always overridden
  //   and is declared as a number type, so I don't think that's true. Fix the text in conditional.rb?
  const result: NumberValue = extractCommonVariableData(ValueType.Number, numberValue['raw-value']);

  const numericCharsOnly = trimmed.replace(/[^0-9-.]/g, '');
  if (numericCharsOnly.length > 0) {
    result.value = numericCharsOnly.includes('.')
      ? parseFloat(numericCharsOnly)
      : parseInt(numericCharsOnly, 10);
    result.errored = !!(
      (controlConfig?.min && result.value < controlConfig.min) ||
      (controlConfig?.max && result.value > controlConfig.max)
    );
  } else {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    result.value = null;
    result.errored = true;
  }

  return result;
}

export function processDateValue(
  dateValue: DateValue,
  _controlConfig?: ControlConfigDatePicker,
  useUtc?: boolean,
): DateValue {
  const result: DateValue = {
    ...extractCommonVariableData(ValueType.Date, dateValue['raw-value']),
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    moment: undefined as string,
  };

  if (!result.variable) {
    result.value = result.value
      .replace(/____/g, DateTime.local().startOf('day').year.toString())
      .replace(/__/g, '01')
      .replace(/_/g, '1');

    const resultMoment = useUtc
      ? DateTime.fromISO(result.value).toUTC()
      : DateTime.fromISO(result.value);
    if (resultMoment.isValid) {
      result.moment = resultMoment.startOf('day').toISO();
    } else {
      result.errored = true;
    }
  }

  // TODO: Add parsing / formatting / validation
  return result;
}

export function processDateTimeValue(
  dateTimeValue: DateTimeValue,
  _controlConfig?: ControlConfigDatePicker,
  useUtc?: boolean,
): DateTimeValue {
  const result: DateTimeValue = {
    ...extractCommonVariableData(ValueType.DateTime, dateTimeValue['raw-value']),
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    moment: undefined as string,
  };

  if (!result.variable) {
    result.value = result.value.replace(/_/g, '0');

    const resultMoment = useUtc
      ? DateTime.fromISO(result.value).toUTC()
      : DateTime.fromISO(result.value);
    if (resultMoment.isValid) {
      result.moment = resultMoment.toISO();
    } else {
      result.errored = true;
    }
  }

  // TODO: Add parsing / formatting / validation
  return result;
}

export function formatDateTimeRangeValue(
  value1: DateTimeValue,
  value2: DateTimeValue,
  includeTime = true,
  useUtc = false,
) {
  const format = includeTime ? 'yyyy-MM-dd HH:mm:ss' : 'yyyy-MM-dd';

  let value1Formatted;
  let value2Formatted;
  if (value1.errored) {
    value1Formatted = '???';
  } else if (value1.variable) {
    value1Formatted = `{{${value1.value}}}`;
  } else {
    value1Formatted = useUtc
      ? DateTime.fromISO(value1.moment).toUTC().toFormat(format)
      : DateTime.fromISO(value1.moment).toFormat(format);
  }

  if (value2.errored) {
    value2Formatted = '???';
  } else if (value2.variable) {
    value2Formatted = `{{${value2.value}}}`;
  } else {
    value2Formatted = useUtc
      ? DateTime.fromISO(value2.moment).toUTC().toFormat(format)
      : DateTime.fromISO(value2.moment).toFormat(format);
  }

  return `${value1Formatted} – ${value2Formatted}`;
}

export enum Control {
  None = 'none',
  Input = 'input',
  Select = 'select',
  NumberPicker = 'number_picker',
  // NumberRange = 'number_range',
  DatePicker = 'date_picker',
  // DateRangePicker = 'date_range_picker',
  DateTimePicker = 'date_time_picker',
  DateTimeRangePicker = 'date_time_range_picker',
  // TimeFramePicker
  TimeFramePicker = 'time_frame_picker',
}

export interface ControlConfigNone {
  type: Control.None;
}

export interface ControlConfigInput {
  type: Control.Input;
  multi?: boolean;
}

export interface ControlConfigNumberPicker {
  type: Control.NumberPicker;
  min?: number;
  max?: number;
  multi?: boolean;
}

export interface ControlConfigDatePicker {
  type: Control.DatePicker;
  multi?: boolean;
}

export interface ControlConfigDateTimePicker {
  type: Control.DateTimePicker;
  multi?: boolean;
}

export interface ControlConfigDateTimeRangePicker {
  type: Control.DateTimeRangePicker;
  multi?: boolean;
  // fix the time to be the beginning of the startDate (00:00:00.000 UTC) to the end of the endDate (23:59:59.999 UTC)
  fixTime?: boolean;
}

export interface ControlConfigSelect {
  type: Control.Select;
  options: { label: string; value: string }[];
  multi?: boolean;
  allOption?: { label: string; value: string };
}

export interface ControlConfigTimeFramePicker {
  type: Control.TimeFramePicker;
  multi?: boolean;
  rangeValueType: ValueType.DateTime | ValueType.Date;
}

export type ControlConfig =
  | ControlConfigDatePicker
  | ControlConfigDateTimePicker
  | ControlConfigDateTimeRangePicker
  | ControlConfigInput
  | ControlConfigNone
  | ControlConfigNumberPicker
  | ControlConfigSelect
  | ControlConfigTimeFramePicker;
